import { IconArrowsMaximize, IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import type { VolumeResponse, VolumeStatus } from '@xver/apis/v1/kufer/volumes/model';
import { Button } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { formatDate } from '@xver/utils/date';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductAccess } from '@xver/hooks/useActiveProject';
import { usePricingQuery } from '@xver/hooks/usePricingQuery';

import AttachedVolumesPanel from '../../BlockStoragePage/components/AttachedVolumesPanel';

import { useVolumesQuery } from '../hooks/useVolumesQuery';
import {
    formatBaht,
    resolveVolumeRate,
    totalGiB,
    totalMonthlyCost,
    volumeMonthlyCost,
} from '../utils/volumeCost';
import { MAX_VOLUMES_PER_CONTAINER } from '../utils/volumeValidation';
import AddVolumeModal from './AddVolumeModal';
import DeleteVolumeModal from './DeleteVolumeModal';
import ResizeVolumeModal from './ResizeVolumeModal';

interface VolumesTabPanelProps {
    containerId: string;
    containerName: string;
    /** False with view-only access: volumes are listed without add, resize or delete. */
    canManage: boolean;
}

const STATUS_STYLES: Record<VolumeStatus, { color: string; backgroundColor: string }> = {
    pending: { color: 'var(--color-status-warning)', backgroundColor: 'rgba(251, 191, 36, 0.15)' },
    bound: { color: 'var(--color-status-info)', backgroundColor: 'rgba(96, 165, 250, 0.15)' },
    attached: { color: 'var(--color-status-teal)', backgroundColor: 'rgba(45, 212, 191, 0.15)' },
    resizing: { color: 'var(--color-status-violet)', backgroundColor: 'rgba(167, 139, 250, 0.15)' },
    failed: { color: 'var(--color-status-danger)', backgroundColor: 'rgba(248, 113, 113, 0.15)' },
    deleting: {
        color: 'var(--color-status-neutral)',
        backgroundColor: 'rgba(156, 163, 175, 0.15)',
    },
};

/** A volume in one of these states is mid-operation, so row actions would race the backend. */
const BUSY_STATUSES: VolumeStatus[] = ['pending', 'resizing', 'deleting'];

export default function VolumesTabPanel({
    containerId,
    containerName,
    canManage,
}: VolumesTabPanelProps) {
    const { t, i18n } = useTranslation('containerEngineInstance');
    const [addOpened, setAddOpened] = useState(false);
    const [resizeTarget, setResizeTarget] = useState<VolumeResponse | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<VolumeResponse | null>(null);

    const { data, isPending } = useVolumesQuery(containerId);
    const allVolumes = data?.volumes ?? [];
    const volumes = allVolumes.filter((volume) => volume.kind !== 'block');
    const blockStorageAccess = useProductAccess('block_storage');

    // resolveVolumeRate guards the placeholder/stale-API case, so this can never render NaN.
    const { data: pricing } = usePricingQuery();
    const volumeRate = resolveVolumeRate(pricing?.products.container.rates.volume_gb_monthly_rate);
    const sizes = volumes.map((volume) => volume.size_gib);
    const limitReached = allVolumes.length >= MAX_VOLUMES_PER_CONTAINER;

    const statusLabels: Record<VolumeStatus, string> = {
        pending: t('volumes.statusPending', 'Provisioning'),
        bound: t('volumes.statusBound', 'Ready'),
        attached: t('volumes.statusAttached', 'Attached'),
        resizing: t('volumes.statusResizing', 'Resizing'),
        failed: t('volumes.statusFailed', 'Failed'),
        deleting: t('volumes.statusDeleting', 'Deleting'),
    };

    const columns: DataTableColumn<VolumeResponse>[] = [
        {
            key: 'name',
            label: t('volumes.colName', 'Name'),
            render: (v) => <span className="font-medium text-fg text-sm">{v.name}</span>,
        },
        {
            key: 'mountPath',
            label: t('volumes.colMountPath', 'Mount path'),
            render: (v) => <span className="font-mono text-xs text-fg/80">{v.mount_path}</span>,
        },
        {
            key: 'size',
            label: t('volumes.colSize', 'Size'),
            render: (v) => (
                <span className="font-mono text-xs text-fg/80">
                    {t('volumes.sizeValue', { size: v.size_gib, defaultValue: '{{size}} GiB' })}
                </span>
            ),
        },
        {
            key: 'cost',
            label: t('volumes.colCost', 'Cost / month'),
            render: (v) => (
                <span className="font-mono text-xs text-fg/80">
                    {t('volumes.costValue', {
                        amount: formatBaht(volumeMonthlyCost(v.size_gib, volumeRate)),
                        defaultValue: '{{amount}} ฿',
                    })}
                </span>
            ),
        },
        {
            key: 'status',
            label: t('volumes.colStatus', 'Status'),
            render: (v) => (
                <div className="flex flex-col items-start gap-1">
                    <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
                        style={STATUS_STYLES[v.status]}
                    >
                        {statusLabels[v.status]}
                    </span>
                    {v.status === 'failed' && v.status_reason && (
                        <span className="text-xs text-fg/68">{v.status_reason}</span>
                    )}
                </div>
            ),
        },
        {
            key: 'created',
            label: t('volumes.colCreated', 'Created'),
            render: (v) => (
                <span className="text-xs text-fg/68">
                    {formatDate(v.created_at, i18n.language, t)}
                </span>
            ),
        },
    ];

    return (
        <>
            <DialogBox
                label={t('volumes.title', 'Block storage')}
                description={t(
                    'volumes.tabDescription',
                    'Persistent volumes attached to this container.'
                )}
                rightDescription={
                    canManage && (
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconPlus size={14} />}
                            disabled={limitReached}
                            title={
                                limitReached
                                    ? t('volumes.limitReached', {
                                          max: MAX_VOLUMES_PER_CONTAINER,
                                          defaultValue:
                                              'A container can have at most {{max}} volumes.',
                                      })
                                    : undefined
                            }
                            onClick={() => setAddOpened(true)}
                        >
                            {t('volumes.add', 'Add volume')}
                        </Button>
                    )
                }
            >
                <div className="w-full flex flex-col gap-3">
                    {volumes.length > 0 && (
                        <div className="w-full flex flex-row items-start gap-2 p-3 rounded-md border border-fg/15 bg-fg/5">
                            <IconInfoCircle size={16} className="shrink-0 mt-0.5 text-fg/50" />
                            <div className="flex flex-col gap-1">
                                <p className="text-xs text-fg/68">
                                    {t(
                                        'volumes.singleInstanceNote',
                                        'This container runs as a single instance while it has volumes attached. Scale-to-zero and replica changes are unavailable.'
                                    )}
                                </p>
                                <p className="text-xs font-medium text-fg/80">
                                    {t('volumes.storageTotal', {
                                        size: totalGiB(sizes),
                                        amount: formatBaht(totalMonthlyCost(sizes, volumeRate)),
                                        defaultValue: 'Storage: {{size}} GiB · ≈{{amount}} ฿/month',
                                    })}
                                </p>
                                <p className="text-xs text-fg/68">
                                    {t('volumes.unitNote', {
                                        rate: formatBaht(volumeRate),
                                        defaultValue:
                                            'Billed per decimal GB at {{rate}} ฿ / GB-month — 1 GiB is 1.074 GB. No free tier.',
                                    })}
                                </p>
                            </div>
                        </div>
                    )}
                    <DataTable
                        caption={t('volumes.title', 'Block storage')}
                        columns={columns}
                        rows={volumes}
                        isLoading={isPending}
                        rowKey={(v) => v.id}
                        emptyMessage={t('volumes.empty', 'No volumes yet.')}
                        actions={
                            canManage
                                ? (v) => {
                                      const busy = BUSY_STATUSES.includes(v.status);
                                      const busyTitle = busy
                                          ? t(
                                                'volumes.busyAction',
                                                'Unavailable while the volume is being provisioned, resized or deleted.'
                                            )
                                          : undefined;
                                      return (
                                          <div className="flex justify-end gap-1">
                                              <button
                                                  type="button"
                                                  className="p-1 rounded hover:bg-fg/8 transition-colors text-fg/70 hover:text-fg disabled:opacity-50"
                                                  aria-label={t(
                                                      'volumes.resizeAction',
                                                      'Resize volume'
                                                  )}
                                                  title={
                                                      busyTitle ??
                                                      t('volumes.resizeAction', 'Resize volume')
                                                  }
                                                  disabled={busy}
                                                  onClick={() => setResizeTarget(v)}
                                              >
                                                  <IconArrowsMaximize size={16} stroke={1.5} />
                                              </button>
                                              <button
                                                  type="button"
                                                  className="p-1 rounded hover:bg-fg/8 transition-colors text-red-400 light:text-red-700 disabled:opacity-50"
                                                  aria-label={t(
                                                      'volumes.deleteAction',
                                                      'Delete volume'
                                                  )}
                                                  title={
                                                      busyTitle ??
                                                      t('volumes.deleteAction', 'Delete volume')
                                                  }
                                                  disabled={busy}
                                                  onClick={() => setDeleteTarget(v)}
                                              >
                                                  <IconTrash size={16} stroke={1.5} />
                                              </button>
                                          </div>
                                      );
                                  }
                                : undefined
                        }
                    />
                </div>
            </DialogBox>

            {(blockStorageAccess === 'view' || blockStorageAccess === 'manage') && (
                <div className="w-full mt-4">
                    <AttachedVolumesPanel
                        target={{ type: 'container', containerId, name: containerName }}
                        canManageTarget={canManage}
                    />
                </div>
            )}

            <AddVolumeModal
                containerId={containerId}
                opened={addOpened}
                onClose={() => setAddOpened(false)}
            />

            <ResizeVolumeModal
                containerId={containerId}
                volume={resizeTarget}
                opened={resizeTarget !== null}
                onClose={() => setResizeTarget(null)}
            />

            <DeleteVolumeModal
                containerId={containerId}
                volume={deleteTarget}
                opened={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
            />
        </>
    );
}
